import React, { type FC, useState, useEffect, useContext } from "react"
import DashboardNotes from "../../pages/dashboard/DashboardNotes"
import Greeting from "../dashboard/Greeting"
import MemberScheduling from "./MemberScheduling"
import { NotificationCenter } from "../notifications/NotificationCenter"
import topBackgroundGradient from "../../assets/images/background-gradient.png"
import { DashboardContacts } from "../../pages/dashboard/DashboardContacts"
import {
  ContactRequests,
  NotificationRequests,
  SessionsRequests,
} from "../../api/app.service"
import { NotificationCtx } from "../../context/notificationContext"
import { SvgNotebook } from "../icons"
import { useAuthenticatedUser } from "../../hooks/useAuthenticatedUser"
import { UserCtx } from "../../context/userContext"
import { useToast } from "../../hooks/useToast"

const MemberDashboard: FC = () => {
  const [windowWidth, setWindowWidth] = useState(window.innerWidth)
  const [userContacts, setUserContacts] = useState<any>([])
  const { user } = useContext(UserCtx)
  const authenticatedUser = useAuthenticatedUser(user)
  const [sessionList, setSessionList] = useState<any[]>([])
  const [activeJoinButton, setActiveJoinButton] = useState(false)
  const [notifications, setNotifications] = useState<any[]>([])
  const [pickerOpen, setPickerOpen] = useState<boolean>(false)

  const { setInboxNotifications, setContactsNotifications, notificationAlert } =
    useContext(NotificationCtx)
  const [isNewNotificationAlert] = useState<boolean>(
    notificationAlert.message !== ""
  )
  const { toast } = useToast()

  const itemStyle = {
    backgroundColor: "#fff",
    borderRadius: "20px",
    boxShadow: "0 0 20px 5px rgba(150, 150, 150, 0.2)",
  }

  const getNotifications = () => {
    if (authenticatedUser) {
      NotificationRequests.getNotifications({
        profile_id: user.activeProfileId,
      })
        .then((data) => {
          const filteredNotifications = data.notifications.filter((n) => {
            return n.notification_category !== "messages"
          })
          setNotifications(filteredNotifications)
          const unreadContactNotifications = data.notifications.filter((n) => {
            return n.slug === "received_connection_request" && n.viewed === 0
          })
          setContactsNotifications(unreadContactNotifications)
          const unreadInboxNotifications = data.notifications.filter((n) => {
            return n.notification_category === "messages" && n.viewed === 0
          })
          setInboxNotifications(unreadInboxNotifications)
        })
        .catch((ex) => {
          toast.error(ex.response.data.message)
        })
    }
  }

  const getContacts = () => {
    if (user?.coachProfile) {
      ContactRequests.getContactList({
        profile_id: user.activeProfileId,
        profile_type: user.activeProfile,
      })
        .then((data) => {
          setUserContacts(data.contact_list)
        })
        .catch((ex) => {
          toast.error(ex.response.data.message)
        })
    }
  }

  const getSessionList = () => {
    if (user) {
      SessionsRequests.get100msSessionList({
        profile_id: user.activeProfileId,
      })
        .then((data) => {
          setSessionList(data.session_list)
          setActiveJoinButton(data?.session_list[0]?.host_joined)
        })
        .catch((ex) => {
          toast.error(ex.response.data.message)
        })
    }
  }

  useEffect(() => {
    getNotifications()
    getContacts()
    getSessionList()
  }, [])

  useEffect(() => {
    getNotifications()
    getContacts()
    getSessionList()
  }, [notificationAlert.show])

  useEffect(() => {
    if (isNewNotificationAlert) {
      getNotifications()
    }
  }, [isNewNotificationAlert])

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth)
    }

    window.addEventListener("resize", handleResize)

    return () => {
      window.removeEventListener("resize", handleResize)
    }
  }, [])

  return (
    <>
      <div
        className="bg-cover flex flex-col items-center justify-center md:-mt-40"
        style={{
          backgroundImage: `url(${topBackgroundGradient})`,
        }}
      >
        <div className="flex w-[80%] xl:w-full xl:max-w-screen-xl mt-8 md:mt-32 justify-center md:justify-start">
          {" "}
          <div className="flex w-full md:w-auto">
            <Greeting />
          </div>
        </div>

        <div
          className={`grid justify-center gap-[30px] mt-[40px] 
      grid-cols-1 
      md:grid-cols-1 
      lg:grid-cols-1 
      last:mb-[40px] max-w-screen-xl`}
        >
          <div className="w-full h-[90%] p-2">
            <div className="-mt-12 w-full justify-between p-2 flex">
              <h2 className="flex flex-row items-center gap-2 text-lg font-bold">
                My Calendar
              </h2>
            </div>
            <div className="w-full h-full rounded-xl h-full">
              <MemberScheduling
                isTodayPage
                pickerOpen={pickerOpen}
                setPickerOpen={setPickerOpen}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-[30px]   p-2">
            <div className="h-[355px] bg-white rounded-xl shadow-[0px_4px_6px_rgba(0,0,0,0.05),0px_-4px_6px_rgba(0,0,0,0.05),4px_0px_6px_rgba(0,0,0,0.05),-4px_0px_6px_rgba(0,0,0,0.05)]">
              <NotificationCenter />
            </div>

            <div className="mt-4 md:mt-0 h-[355px] bg-white rounded-xl shadow-[0px_4px_6px_rgba(0,0,0,0.05),0px_-4px_6px_rgba(0,0,0,0.05),4px_0px_6px_rgba(0,0,0,0.05),-4px_0px_6px_rgba(0,0,0,0.05)]">
              <DashboardContacts userContacts={userContacts} />
            </div>

            {windowWidth >= 1280 && (
              <div className="w-[400px] h-[530px] flex flex-col justify-center items-center rounded-xl bg-white -mt-[176px] shadow-[0px_4px_6px_rgba(0,0,0,0.05),0px_-4px_6px_rgba(0,0,0,0.05),4px_0px_6px_rgba(0,0,0,0.05),-4px_0px_6px_rgba(0,0,0,0.05)]">
                <div className="flex justify-start items-start w-full">
                  <h2 className="flex gap-2 font-bold text-lg items-center -mt-8 mb-1 ml-2">
                    <SvgNotebook />
                    My Notes
                  </h2>
                </div>
                <DashboardNotes userContacts={userContacts} />
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  )
}

export default MemberDashboard
